/**
 * AR-380 — the Account center's sample record.
 *
 * The ticket builds the front end only: "Data is wired in the companion ticket
 * (AR-381), so build against the prototype's sample data." This is the v13
 * prototype's `ACCT_DEFAULTS`, typed. Every page reads THIS record and nothing
 * else, so a value cannot be typed twice on a page — when AR-381 lands it
 * swaps the source behind `useAccountRecord`, not the pages.
 */

export type AccountRole = 'Owner' | 'Manager' | 'Associate Advisor';

export interface AccountAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface AccountSocialLink {
  ty: string;
  h: string;
}

export interface AccountWritingSample {
  kind: string;
  title: string;
  words: number;
  added: string;
}

export interface AccountTeamMember {
  /**
   * `users.id`, present once the migrated model has answered — the Manage
   * pop-up's status write addresses a member by id, not by list position.
   * Absent on the sample record, which has no rows in any database.
   */
  id?: number;
  first: string;
  last: string;
  email: string;
  role: AccountRole;
  status: 'Active' | 'Deactivated';
  seen: string;
}

export interface AccountRecord {
  you: {
    sal: string;
    first: string;
    last: string;
    nick: string;
    email: string;
    ph1t: string;
    ph1: string;
    ph2t: string;
    ph2: string;
    tz: string;
    pwChanged: string;
    role: AccountRole;
  };
  addr: AccountAddress;
  bill: AccountAddress & { same: boolean; card: string; last4: string; exp: string };
  pub: {
    photo: string;
    first: string;
    last: string;
    title: string;
    headline: string;
    bio: string;
    since: string;
    awards: string[];
    social: AccountSocialLink[];
  };
  pubmeta: { dirty: boolean; published: string };
  spec: {
    dest: string[];
    styles: string[];
    interests: string[];
    budget: string;
    duration: string;
    party: string;
  };
  comm: { nonair: number; air: number; fees: number };
  biz: AccountAddress & {
    brand: string;
    legal: string;
    type: string;
    work: string;
    tagline: string;
    desc: string;
    since: string;
    addrSame: boolean;
  };
  cta: { email: string; phone: string; city: string; state: string; cta: string };
  web: {
    mode: 'sites' | 'ext';
    plan: string;
    url: string;
    pages: string[];
    promos: number;
    emailplus: boolean;
  };
  team: AccountTeamMember[];
  teamset: { crm: string; vault: string; brand: string; terms: string };
  crm: { gmail: boolean; taskDays: number; stallDays: number; importedOn: string };
  prop: {
    welcome: string;
    closing: string;
    termsMode: 'own' | 'team';
    termsLink: string;
    termsText: string;
    hidePrices: boolean;
    cta: string;
  };
  vault: { payEmail: string; keySet: string };
  email: { digest: boolean; promos: string; events: boolean };
  notif: { rows: Record<string, (number | null)[]> };
  voice: {
    tone: string;
    style: string;
    notes: string;
    assist: string;
    samples: AccountWritingSample[];
  };
  ai: { memory: boolean; voice: boolean; cleared: string; export: { on: string; status: string } | null };
  /** Workspace preferences. Text size scales the whole center, not the text alone. */
  ws: { textsize: 'Default' | 'Larger' | 'Largest' };
}

/**
 * The advisor's non-air split. The prototype reads the platform's one split
 * canon here so Finances and the Commission table cannot state two different
 * numbers; until AR-381 wires that source, the sample figure stands.
 */
const SAMPLE_NONAIR_SPLIT = 70;

export const ACCOUNT_DEFAULTS: AccountRecord = {
  you: {
    sal: 'Ms.',
    first: 'Samantha',
    last: 'Brooks',
    nick: 'Sam',
    email: 'samantha@blueharbortravel.com',
    ph1t: 'Mobile',
    ph1: '(843) 555-0184',
    ph2t: 'Office',
    ph2: '(843) 555-0142',
    tz: 'Eastern',
    pwChanged: 'Apr 16, 2026',
    role: 'Owner',
  },
  // Three addresses, three records: the personal mailing address here, the
  // billing address with the payment method, the business address with the
  // business.
  addr: { street: '14 Queen St', city: 'Charleston', state: 'SC', zip: '29401' },
  bill: {
    same: true,
    street: '',
    city: '',
    state: '',
    zip: '',
    card: 'Visa',
    last4: '3308',
    exp: '09/28',
  },
  pub: {
    photo: '',
    // Two fields, not one: a surface can greet her by the first name without
    // splitting a string.
    first: 'Samantha',
    last: 'Brooks',
    // `title` renders as Headline (beside the name), `headline` as Short
    // introduction (profile cards and search results), `bio` as Full bio.
    title: 'Luxury and Family Travel Advisor',
    headline:
      'Helping travelers plan unforgettable beach, cruise, and Europe vacations. With a passion for creating meaningful travel experiences, I take the time to understand your style, preferences, and goals.',
    bio: 'I plan for families traveling together, couples marking an anniversary, and small groups who want one person holding all the details. Most of my trips are beach weeks, cruises, and two-week runs through Europe.\n\nWe start with a call about how you like to travel rather than a list of hotels. I send one proposal with the options I think fit, we adjust it until it is right, and I handle the bookings, the payment schedule and the paperwork from there. While you are away you have my mobile number.\n\nI have sailed Alaska and the Mediterranean, driven the Amalfi Coast and the Irish coast road, and spent time in Kyoto, Santorini, Maui and most of the Caribbean. I go back to the places I sell so I can tell you what a room, a ship or a shore day is actually like.',
    since: '2014',
    awards: [
      'CLIA Certified Cruise Counsellor',
      'Sandals Certified Specialist',
      'TLN Luxury specialist',
    ],
    social: [
      { ty: 'Instagram', h: '@blueharbortravel' },
      { ty: 'Facebook', h: '/blueharbortravel' },
      { ty: 'LinkedIn', h: '/in/samanthabrooks' },
      { ty: 'Website', h: 'blueharbortravel.com' },
    ],
  },
  // A Save on a public card makes a draft; Publish changes makes it live.
  pubmeta: { dirty: false, published: 'Jul 12' },
  spec: {
    dest: ['Italy', 'Greece', 'Caribbean', 'Hawaii', 'Europe'],
    styles: ['Family vacations', 'Cruises', 'All-inclusive resorts', 'Luxury'],
    interests: ['Beach', 'Food and wine', 'Celebrations', 'Multigenerational'],
    budget: '$10,000 – $25,000',
    duration: '7–10 nights',
    party: '2–6 travelers',
  },
  // The ACTUAL rates, by booking type — never a plan default.
  comm: { nonair: SAMPLE_NONAIR_SPLIT, air: 50, fees: 100 },
  biz: {
    brand: 'Blue Harbor Travel',
    legal: 'Brooks Travel Design LLC',
    type: 'Limited Liability Company',
    work: 'Full-time',
    tagline: 'Adventure Is Calling',
    desc: 'Blue Harbor Travel plans beach, cruise and Europe vacations for families who travel together.',
    since: '2019',
    addrSame: true,
    street: '',
    city: '',
    state: '',
    zip: '',
  },
  cta: {
    email: 'hello@blueharbortravel.com',
    phone: '(843) 555-0142',
    city: 'Charleston',
    state: 'SC',
    cta: "Let's plan your next adventure",
  },
  web: {
    mode: 'sites',
    plan: 'Essentials',
    url: '',
    pages: ['Home', 'Trip Inspiration', 'Blog', 'Contact'],
    promos: 6,
    emailplus: false,
  },
  // The team, less the owner — accountMembers() puts her at the head of it.
  team: [
    {
      first: 'Daniel',
      last: 'Reyes',
      email: 'daniel@blueharbortravel.com',
      role: 'Associate Advisor',
      status: 'Active',
      seen: 'Yesterday',
    },
  ],
  // The values are the access level itself, so a product page can print the
  // rule without translating it.
  teamset: {
    crm: 'Private to each advisor',
    vault: 'Private to each advisor',
    brand: "Each advisor's own brand",
    terms: "Each advisor's own terms",
  },
  crm: { gmail: false, taskDays: 2, stallDays: 14, importedOn: "Mar '24" },
  prop: {
    welcome:
      'Thank you for letting Blue Harbor Travel plan this trip. Everything here is built around what you told me you wanted, and I am one message away if anything needs changing.',
    closing: 'With love, Samantha and the Blue Harbor Travel team.',
    termsMode: 'own',
    termsLink: 'blueharbortravel.com/terms',
    // One \n\n-joined string: the row counts the paragraphs and the preview
    // splits them, so the count cannot disagree with the text.
    termsText:
      'I plan the trip, book it with the suppliers, and stay with it until you are home. Anything you book yourself sits outside this agreement, and I cannot change or refund it for you.\n\nA deposit is due when you approve the trip and is not refundable once a supplier confirms. The rest is due on the date shown on your payment schedule, and missing that date can release the booking.\n\nEvery supplier sets its own change and cancellation fees. I will quote you the exact cost before anything is changed or cancelled, and nothing moves without your approval.\n\nYou are responsible for passports, visas and entry requirements. I will tell you what each trip needs and check what I can. Travel insurance is offered on every trip, and declining it is recorded with your approval.',
    hidePrices: false,
    cta: 'Review terms and approve',
  },
  vault: {
    payEmail:
      'Hi {first name}, here is the secure link to authorize your card for this trip. It takes about a minute and nothing is charged today.',
    keySet: "Mar '24",
  },
  email: { digest: true, promos: 'Weekly', events: true },
  // One row per event, three channels (in-app, email, SMS). null = the channel
  // does not carry that event. The required rows are not here — they cannot be set.
  notif: {
    rows: {
      tasks: [1, 1, 0],
      proposals: [1, 1, 0],
      payments: [1, 1, 0],
      team: [1, 0, 0],
      product: [1, 0, null],
    },
  },
  voice: {
    tone: 'Friendly',
    style: 'Storytelling',
    notes: '',
    assist: 'Balanced',
    samples: [
      { kind: 'Email', title: 'Amalfi proposal cover note', words: 184, added: 'Jun 18, 2026' },
      { kind: 'Email', title: 'Nakamura final payment reminder', words: 96, added: 'Jul 2, 2026' },
      {
        kind: 'Blog post',
        title: 'Alaska 2027 — the season to book early',
        words: 742,
        added: 'Jul 14, 2026',
      },
      { kind: 'Itinerary', title: 'Kyoto and Hakone, day by day', words: 1180, added: 'Jul 21, 2026' },
    ],
  },
  ai: { memory: true, voice: true, cleared: '', export: null },
  ws: { textsize: 'Default' },
};

// ---------------------------------------------------------------------------
// The facts another surface owns. The prototype reads each of these off the
// page that owns it (the Suppliers list, the Payments vault, the Sites
// storefront, the GEM page) so two surfaces cannot disagree. AR-381 points them
// at the migrated record; until then they are the prototype's own figures.
// ---------------------------------------------------------------------------

export const ACCOUNT_SITE_DOMAIN = 'blueharbortravel.com';
export const ACCOUNT_SITE_PRICES: Record<string, number> = {
  Essentials: 29,
  Professional: 59,
};
export const ACCOUNT_VAULT_CARDS = 7;
export const ACCOUNT_VAULT_EXPIRED = 1;
export const ACCOUNT_PREFERRED_SUPPLIERS = [
  'Princess Cruises',
  'Sandals Resorts',
  'AmaWaterways',
  'Belmond',
  'Viking',
];

/**
 * The GEM ladder. v15 made GEM Status a page of its own inside Account
 * (D-ACCT-26), so the page needs the whole ladder rather than the one summary
 * line the old side panel showed. Binding these to finance is a separate
 * ticket; the shape is what the page is built against.
 */
export const ACCOUNT_GEM_GROUPS: readonly {
  group: string;
  sub: string;
  levels: readonly (readonly [string, number | string])[];
}[] = [
  {
    group: 'Starter',
    sub: 'Getting started',
    levels: [['Crystal', 0], ['Opal', '1st booking'], ['Amber', 2500]],
  },
  {
    group: 'Builder',
    sub: 'Building momentum',
    levels: [['Turquoise', 5000], ['Topaz', 7500], ['Peridot', 10000]],
  },
  {
    group: 'Emerging',
    sub: 'Gaining recognition',
    levels: [['Pearl', 12000], ['Amethyst', 17000], ['Onyx', 22000]],
  },
  {
    group: 'Crown Club',
    sub: 'Top 5% network-wide',
    levels: [['Sapphire', 41000], ['Emerald', 54000], ['Ruby', 78000]],
  },
  {
    group: 'Diamond Club',
    sub: 'Top 1% network-wide',
    levels: [
      ['White Diamond', 123000],
      ['Pink Diamond', 165000],
      ['Black Diamond', 210000],
    ],
  },
];

export const ACCOUNT_GEM_LADDER: readonly (readonly [string, number | string])[] =
  ACCOUNT_GEM_GROUPS.flatMap((g) => g.levels);

export const ACCOUNT_GEM_ME = {
  level: 'Emerald',
  ytd: 63900,
  get lvlNo() {
    return ACCOUNT_GEM_LADDER.findIndex(([n]) => n === this.level) + 1;
  },
};

/** Commission outstanding on open invoices — the gauge's second number. */
export const ACCOUNT_GEM_OPEN_COMMISSION = 1480;
export const ACCOUNT_GEM_OPEN_INVOICES = 6;

export const ACCOUNT_PLAN = 'Growth';
export const ACCOUNT_PLAN_PRICE = 29;
export const ACCOUNT_RENEWAL = 'Aug 1, 2026';
export const ACCOUNT_MEMBER_SINCE = "Mar '24";

/** What the plan adds that a lower one does not. */
export const ACCOUNT_PLAN_INCLUDES: readonly { t: string; s: string }[] = [
  { t: 'Proposal Builder', s: 'Unlimited proposals with your own branding and terms' },
  { t: 'Payments + CardVault', s: 'Stored cards and client payment authorizations' },
  { t: 'WorldVia Sites', s: 'A website with Dynamic Promotions, billed separately' },
  { t: 'Priority Member Success', s: 'A named contact and a faster response time' },
];

export const ACCOUNT_SHOWCASE_URL = 'worldvia.com/advisors/samantha-brooks';
export const ACCOUNT_PROFILER_URL = 'https://www.travelleaders.com/agent/124324124';
/** The Agent Profiler listing needs attention — drives an Action required row. */
export const ACCOUNT_PROFILER_NEEDS_ATTENTION = true;
